import { useState } from "react"
import { Box, Stack, Typography, Divider } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserTie, faBuilding, faLocationDot } from "@fortawesome/pro-regular-svg-icons"
import { faScreencast } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { MeetingListPagination } from "./MeetingListPagination"
import React from "react"
import { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { getHostOrganizerCompanyName, getHostOrganizerName } from "./helpers"

const useStyles = makeStyles()(() => ({
    timeBox: {
        paddingLeft: "1rem",
        borderLeft: "3px solid #BFDBFE",
    },
    detailRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        color: tailwindPalette.slate[500],
        fontSize: "0.875rem",
    },
    divider: {
        marginTop: "0.5rem",
        borderColor: "#E2E8F0",
    },
    item: {
        padding: ".5rem",
        cursor: "pointer",
        marginBottom: ".1rem",
        marginTop: ".3rem",
        border: "1px solid transparent",
    },
    hoverBlue: {
        "&:hover": {
            borderRadius: ".75rem",
            border: `1px solid ${tailwindPalette.blue[500]}`,
            backgroundColor: tailwindPalette.blue[50],
        },
    },
    hoverGray: {
        "&:hover": {
            borderRadius: ".75rem",
            border: `1px solid ${tailwindPalette.slate[300]}`,
            backgroundColor: tailwindPalette.slate[100],
        },
    },
    activeItem: {
        borderRadius: "0.75rem",
        border: `1px solid ${tailwindPalette.blue[500]}`,
        backgroundColor: tailwindPalette.blue[50],
    },
}))

export function MeetingList({
    meetings = [],
    selectedMeeting,
    onMeetingClick,
}: {
    meetings?: ServiceCenterBooking[]
    onMeetingClick: (item: ServiceCenterBooking) => void
    selectedMeeting: ServiceCenterBooking | null
}) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const paginatedMeetings = meetings.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)

    return (
        <Stack spacing="1rem">
            {paginatedMeetings.map((meeting: ServiceCenterBooking) => (
                <React.Fragment key={meeting.id}>
                    <Box
                        className={cx(
                            classes.item,
                            selectedMeeting?.id === meeting.id ? classes.hoverBlue : classes.hoverGray,
                            { [classes.activeItem]: selectedMeeting?.id === meeting.id },
                        )}
                        onClick={() => onMeetingClick(meeting)}
                    >
                        <Stack direction="row" alignItems="center" spacing="1rem">
                            <Box>
                                <Typography variant="subtitle1">
                                    {dateTimeFactory.formatTime(
                                        dateTimeFactory.fromApi(meeting?.meetingDetails?.startDateTime),
                                    )}
                                </Typography>
                                <Typography variant="body2" component="span" color="textSecondary">
                                    {dateTimeFactory.formatTime(
                                        dateTimeFactory.fromApi(meeting?.meetingDetails?.endDateTime),
                                    )}
                                </Typography>
                            </Box>

                            <Stack className={classes.timeBox} direction="column" spacing=".5rem">
                                <Stack direction="row" alignItems="center" spacing=".5rem">
                                    <Typography variant="subtitle1">{meeting?.meetingDetails?.meetingName}</Typography>
                                    <FontAwesomeIcon icon={faScreencast} />
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing="1rem">
                                    <Box className={classes.detailRow}>
                                        <FontAwesomeIcon icon={faUserTie} />
                                        <Typography variant="body2">
                                            {getHostOrganizerName(meeting?.organizers)}
                                        </Typography>
                                    </Box>
                                    <Box className={classes.detailRow}>
                                        <FontAwesomeIcon icon={faBuilding} />
                                        <Typography variant="body2">
                                            {getHostOrganizerCompanyName(meeting?.organizers)}
                                        </Typography>
                                    </Box>
                                    <Box className={classes.detailRow}>
                                        <FontAwesomeIcon icon={faLocationDot} />
                                        <Typography variant="body2">
                                            {meeting?.meetingDetails?.location ?? "-"}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>
                    <Divider className={classes.divider} />
                </React.Fragment>
            ))}
            <MeetingListPagination
                totalItems={meetings.length}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                setRowsPerPage={setRowsPerPage}
                setCurrentPage={setCurrentPage}
            />
        </Stack>
    )
}
